import { useEffect, useState } from "react";
import axios from "axios";
import { formatDate } from "../lib/formatters";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";

export default function StripePayouts() {
  const [loading, setLoading] = useState(true);
  const [connectStatus, setConnectStatus] = useState(null);
  const [balance, setBalance] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loginLink, setLoginLink] = useState(null);
  const [error, setError] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);

      try {
        // 1) Always fetch Stripe Connect status first
        const statusRes = await axios.get(
          `${API_BASE}/api/stripe/connect/status`,
          { withCredentials: true }
        );
        const status = statusRes.data || {};
        setConnectStatus(status);

        // If landlord is not onboarded at all, stop here – show CTA instead
        if (!status.onboarded) {
          setLoading(false);
          return;
        }

        // 2) If onboarded, load payouts info
        const [balRes, payoutRes, linkRes] = await Promise.all([
          axios.get(`${API_BASE}/api/stripe/payouts/balance`, {
            withCredentials: true,
          }),
          axios.get(`${API_BASE}/api/stripe/payouts/list`, {
            withCredentials: true,
          }),
          axios.get(`${API_BASE}/api/stripe/dashboard-link`, {
            withCredentials: true,
          }),
        ]);

        setBalance(balRes.data);
        setPayouts(payoutRes.data.payouts || []);
        setLoginLink(linkRes.data.url);
      } catch (err) {
        console.error("Failed to load Stripe payout dashboard:", err);
        setError("Unable to load Stripe payout information.");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  async function handleStartOnboarding() {
    try {
      setOnboardingLoading(true);
      const res = await axios.post(
        `${API_BASE}/api/stripe/connect/create`,
        {},
        { withCredentials: true }
      );

      if (res.data?.url) {
        window.location.href = res.data.url; // go to Stripe onboarding
      } else {
        alert("Could not start Stripe onboarding. Please try again.");
      }
    } catch (err) {
      console.error("Stripe connect create error:", err);
      alert(
        err?.response?.data?.error ||
          "Failed to start Stripe onboarding. Please try again."
      );
    } finally {
      setOnboardingLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6 animate-pulse text-gray-500">
        Loading Stripe payouts…
      </div>
    );
  }

  const status = connectStatus || {};
  const { onboarded, chargesEnabled, requirementsDue } = status;
  const safeAvailable = balance?.available || 0;
  const safePending = balance?.pending || 0;

  return (
    <div className="p-8">
      <h1 className="text-3xl font-semibold mb-6">Stripe Payouts</h1>

      {/* Onboarding / status panel */}
      {!onboarded && (
        <div className="mb-8 p-5 border rounded-lg bg-yellow-50 border-yellow-200">
          <h2 className="text-xl font-semibold mb-2">
            Connect Stripe to receive payouts
          </h2>
          <p className="mb-4 text-sm text-gray-700">
            To receive rent payouts into your bank account, you need to connect
            a Stripe Express account.
          </p>
          <button
            onClick={handleStartOnboarding}
            disabled={onboardingLoading}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition disabled:opacity-60"
          >
            {onboardingLoading ? "Redirecting to Stripe…" : "Connect with Stripe"}
          </button>
        </div>
      )}

      {onboarded && !chargesEnabled && (
        <div className="mb-6 p-4 border rounded-lg bg-yellow-50 border-yellow-200">
          <h2 className="text-lg font-semibold mb-1">
            Stripe account pending verification
          </h2>
          <p className="text-sm text-gray-700">
            Your Stripe account is linked, but payouts may be limited until
            verification is complete.
          </p>
          {requirementsDue && (
            <button
              onClick={handleStartOnboarding}
              disabled={onboardingLoading}
              className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-60"
            >
              {onboardingLoading
                ? "Opening Stripe…"
                : "Complete verification on Stripe"}
            </button>
          )}
        </div>
      )}

      {error && onboarded && (
        <div className="mb-6 bg-red-100 text-red-700 border border-red-300 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Only show balances + payouts if onboarded */}
      {onboarded && !error && (
        <>
          {/* Balance Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="p-5 border rounded-lg bg-white shadow-sm">
              <h2 className="text-xl font-semibold mb-2">Available Balance</h2>
              <p className="text-3xl font-bold text-green-700">
                £{(safeAvailable / 100).toFixed(2)}
              </p>
              <p className="text-sm text-gray-500 mt-1">Ready for payout</p>
            </div>

            <div className="p-5 border rounded-lg bg-white shadow-sm">
              <h2 className="text-xl font-semibold mb-2">Pending Balance</h2>
              <p className="text-3xl font-bold text-gray-700">
                £{(safePending / 100).toFixed(2)}
              </p>
              <p className="text-sm text-gray-500 mt-1">Processing by Stripe</p>
            </div>
          </div>

          {/* Payout History */}
          <div className="mb-8 p-5 border rounded-lg bg-white shadow-sm">
            <h2 className="text-xl font-semibold mb-4">Payout History</h2>

            {payouts.length === 0 ? (
              <p className="text-gray-500">No payouts yet.</p>
            ) : (
              <div className="space-y-4">
                {payouts.map((p) => (
                  <div key={p.id} className="border-b pb-3">
                    <p>
                      <strong>Payout ID:</strong> {p.id}
                    </p>
                    <p>
                      <strong>Amount:</strong> £{(p.amount / 100).toFixed(2)}
                    </p>
                    <p>
                      <strong>Arrival Date:</strong>{" "}
                      {formatDate(p.arrival_date * 1000, "TBA")}
                    </p>
                    <p>
                      <strong>Status:</strong>{" "}
                      <span
                        className={`px-2 py-1 rounded text-sm ${
                          p.status === "paid"
                            ? "bg-green-100 text-green-700"
                            : p.status === "pending"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-gray-200 text-gray-700"
                        }`}
                      >
                        {p.status}
                      </span>
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stripe Dashboard Button */}
          {loginLink && (
            <a
              href={loginLink}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition"
            >
              Open Stripe Dashboard
            </a>
          )}
        </>
      )}
    </div>
  );
}
